// Pure conversion helpers for the Lat/Long Converter tool.
//
// Inputs that can't be parsed return NaN (for parsers) or "NaN" (for
// formatters) so the caller can render a user-facing error rather than
// silently producing garbage.

const stripCardinal = (input: string): string =>
  input.replace(/[NE]/gi, '').replace(/[SW]/gi, '-').trim();

const splitParts = (cleaned: string): string[] =>
  cleaned.split(/\s+/).filter((p) => p.length > 0);

/**
 * Format a decimal-degree value as Degrees + decimal Minutes ("60° 12.000'").
 * Sign is rendered as a leading "-" — the integer-degrees field stays
 * positive so e.g. -111.4 → "-111° 24.000'", not "-112° 36.000'".
 */
export function ddToDmm(dd: number): string {
  if (!Number.isFinite(dd)) return 'NaN';
  const sign = dd < 0 ? '-' : '';
  const abs = Math.abs(dd);
  const degrees = Math.floor(abs);
  const minutes = (abs - degrees) * 60;
  return `${sign}${degrees}° ${minutes.toFixed(3)}'`;
}

/**
 * Format a decimal-degree value as Degrees + Minutes + decimal Seconds
 * ("60° 12' 0.00\""). Sign-aware (see ddToDmm).
 */
export function ddToDms(dd: number): string {
  if (!Number.isFinite(dd)) return 'NaN';
  const sign = dd < 0 ? '-' : '';
  const abs = Math.abs(dd);
  const degrees = Math.floor(abs);
  const minutesNotTruncated = (abs - degrees) * 60;
  const minutes = Math.floor(minutesNotTruncated);
  const seconds = (minutesNotTruncated - minutes) * 60;
  return `${sign}${degrees}° ${minutes}' ${seconds.toFixed(2)}"`;
}

/**
 * Parse a Degrees + decimal Minutes string into decimal degrees.
 * Accepts cardinal-prefixed forms ("N60 12", "W111 24") and signed
 * numeric forms ("-111 24"). Returns NaN on malformed input.
 *
 * Sign is taken from the degrees component; minutes are always added
 * to the magnitude so that "-111 24" parses to -111.4 (not -110.6).
 */
export function parseDmmToDd(input: string): number {
  const cleaned = stripCardinal(input);
  const parts = splitParts(cleaned);
  if (parts.length < 2) return NaN;
  const deg = parseFloat(parts[0]);
  const min = parseFloat(parts[1]);
  if (!Number.isFinite(deg) || !Number.isFinite(min)) return NaN;
  const sign = deg < 0 || Object.is(deg, -0) ? -1 : 1;
  return sign * (Math.abs(deg) + min / 60);
}

/**
 * Parse a Degrees + Minutes + decimal Seconds string into decimal
 * degrees. Same sign / cardinal handling as parseDmmToDd.
 */
export function parseDmsToDd(input: string): number {
  const cleaned = stripCardinal(input);
  const parts = splitParts(cleaned);
  if (parts.length < 3) return NaN;
  const deg = parseFloat(parts[0]);
  const min = parseFloat(parts[1]);
  const sec = parseFloat(parts[2]);
  if (!Number.isFinite(deg) || !Number.isFinite(min) || !Number.isFinite(sec)) return NaN;
  const sign = deg < 0 || Object.is(deg, -0) ? -1 : 1;
  return sign * (Math.abs(deg) + min / 60 + sec / 3600);
}

/**
 * Build a GeoJSON Point string for a (lat, lon) decimal-degree pair.
 * Note: GeoJSON coordinates are [longitude, latitude].
 */
export function toGeoJsonPoint(latDD: number, lonDD: number): string {
  return JSON.stringify({ type: 'Point', coordinates: [lonDD, latDD] });
}
